import {
  differenceInMinutes,
  format,
  isBefore,
  isToday,
  isWithinInterval,
  parse,
  setHours,
  setMinutes,
} from 'date-fns';
import { useTranslation } from 'next-i18next';
import { GroupBase } from 'react-select';
import {
  getDecimalPlacesValidation,
  nameIdPairValidationSchema,
} from 'utils/general';
import * as yup from 'yup';

import { CreateEditContract } from '@/modules/workforce/Contracts/components/CreateAndEdit';
import ContractDetailsInputs from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractDetailsInputs';
import ContractReview from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractReview';
import ContractTypeInputs from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractTypeInputs';
import { useContractDetails } from '@/modules/workforce/Contracts/components/Details/logic';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { IMultiStepFormStep } from '@/shared/components/molecules/MultiStepForm/IMultiStepForm';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { clearSecondsAndMilliseconds } from '@/shared/lib/dates';
import { IGeneral } from '@/shared/types';

export const useCreateEditContractUtils = () => {
  const {
    t,
    // i18n: { language },
  } = useTranslation(['common', 'codes']);

  const [selectedAsset] = useStore((store) => store?.asset);

  //********** popup **********

  const { openModal } = useModal();

  const openCreateContractModal = (supplier?: IGeneral.NameIdPair) => {
    openModal(<CreateEditContract supplier={supplier} />, {
      variant: 'full',
      title: <Typography variant='h2'>{t('add-contract')}</Typography>,
      onClose: () => {
        openModal(
          <ConfirmationModal
            header={t('going-somewhere-else')}
            message={t('data-loss-warning')}
            variant='warning'
            dismissText={t('continue-editing')}
            confirmText={t('discard-and-exit')}
            onConfirmRemoveNumber={3}
            buttonsAlignment='col'
            preferredAction='dismiss'
          />
        );
      },
    });
  };

  const openEditContractModal = (supplierAssignmentId: number) => {
    openModal(
      <CreateEditContract supplierAssignmentId={supplierAssignmentId} />,
      {
        variant: 'full',
        title: <Typography variant='h2'>{t('edit-contract')}</Typography>,
        onClose: () => {
          openModal(
            <ConfirmationModal
              header={t('going-somewhere-else')}
              message={t('data-loss-warning')}
              variant='warning'
              dismissText={t('continue-editing')}
              confirmText={t('discard-and-exit')}
              onConfirmRemoveNumber={3}
              buttonsAlignment='col'
              preferredAction='dismiss'
            />
          );
        },
      }
    );
  };

  //********** Steps **********

  const getFormSteps = (
    dailyWage?: number
  ): IMultiStepFormStep<IContracts.IAddContractSchema>[] => [
    {
      title: t('contract-details'),
      childrenTitle: t('contract-details'),
      render: () => <ContractDetailsInputs />,
      fields: ['entity', 'supplier', 'jobs', 'startDate', 'endDate'],
    },
    {
      title: t('contract-type'),
      render: () => <ContractTypeInputs />,
      fields:
        dailyWage === 1
          ? ['dailyWage', 'dailyBased', 'dailyBased.hasExtraTime']
          : ['dailyWage', 'unitBased'],
    },
    {
      title: t('review'),
      render: ({ setStep }) => <ContractReview setStep={setStep} />,
      fields: [
        'entity',
        'supplier',
        'jobs',
        'jobs',
        'startDate',
        'endDate',
        ...(dailyWage === 1
          ? (['dailyWage', 'dailyBased', 'dailyBased.hasExtraTime'] as const)
          : (['dailyWage', 'unitBased'] as const)),
      ],
    },
  ];

  //********** Validation Schema **********
  const editContractValidationSchema = yup.object().shape({
    entity: yup.object().shape({
      name: yup.string().required(),
      id: yup.number().required(),
    }),
    dailyWage: yup
      .number()
      // .test((number) => String(number) === '1' || String(number) === '0')
      .required(t('this-field-is-required')),
    startDate: yup.date(),
    endDate: yup.date(),
    unitBased: yup.array().when('dailyWage', {
      is: 0,
      then: yup
        .array()
        .required(t('contract-atleast-one-unit'))
        .min(1, t('contract-atleast-one-unit'))
        .of(
          yup
            .object()
            .shape({
              unitPrice: getDecimalPlacesValidation(
                t('this-field-accepts-two-decimal-places')
              )
                .moreThan(0, t('This field must be greater than zero.'))
                .required(t('this-field-is-required')),
              unitName: yup.string().required(t('this-field-is-required')),
              unitId: yup.number().required(t('this-field-is-required')),
            })
            .default(undefined)
        ),
    }),
    dailyBased: yup.object().when('dailyWage', {
      is: 1,
      then: yup.object().shape({
        hasExtraTime: yup.boolean(),
        rate: getDecimalPlacesValidation(
          t('this-field-accepts-two-decimal-places')
        )
          .moreThan(0, t('This field must be greater than zero.'))
          .required(t('this-field-is-required')),
        startTime: yup.date().required(t('this-field-is-required')),
        endTime: yup
          .date()
          .required(t('this-field-is-required'))
          .test((endTime, { parent: { startTime }, createError, path }) => {
            if (endTime && startTime) {
              const start = clearSecondsAndMilliseconds(startTime);
              const end = clearSecondsAndMilliseconds(endTime);
              if (isBefore(end, start))
                return createError({
                  path,
                  message: t('CREATE_SUPPLIER_ASSIGNMENT#001', { ns: 'codes' }),
                });
              if (Math.abs(differenceInMinutes(start, end)) < 240)
                return createError({
                  path,
                  message: t('CREATE_SUPPLIER_ASSIGNMENT#002', { ns: 'codes' }),
                });
              if (Math.abs(differenceInMinutes(start, end)) > 720)
                return createError({
                  path,
                  message: t('CREATE_SUPPLIER_ASSIGNMENT#003', { ns: 'codes' }),
                });
            }
            return true;
          }),
        extraHoursPricing: yup.array().when('hasExtraTime', {
          is: true,
          then: (schema) =>
            schema.of(
              yup.object().shape({
                hourPrice: getDecimalPlacesValidation(
                  t('this-field-accepts-two-decimal-places')
                )
                  .required(t('this-field-is-required'))
                  .moreThan(0, t('This field must be greater than zero.')),
              })
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      }),
    }),
  });

  const addContractValidationSchema = editContractValidationSchema.shape({
    supplier: nameIdPairValidationSchema(t).required(),
    jobs: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            label: yup.string().required(),
            value: yup.number().required(),
          })
          .required()
      )
      .min(1, t('this-field-is-required'))
      .required(t('this-field-is-required')),
  });

  //********** Edit Review Card **********

  const sharedCardProps = {
    titleTypography: {
      variant: 'body2' as const,
      className: 'text-surface-10',
    },
    descriptionTypography: {
      weight: 'medium' as const,
    },
  };

  const getContractGeneralInfoCardItems = (
    contract: IContracts.IContractDetails
  ): IIconWithTitleDescriptionProps[] => [
    {
      ...sharedCardProps,
      title: t('crew'),
      description: contract.supplierName,
    },
    {
      ...sharedCardProps,
      title: t('operations'),
      description: (
        <PartialListWithTooltip
          items={[
            {
              description: contract.jobName,
              imageURL: contract.cropImage,
            },
          ]}
        />
      ),
    },
    {
      ...sharedCardProps,
      title: t('farm'),
      description: selectedAsset?.name,
    },
    {
      ...sharedCardProps,
      title: t('contract-type'),
      description:
        contract.dailyWage === 1 ? t('daily-rate') : t('piecework-rate'),
    },
  ];

  // ********** Mutation **********/

  const getEditMutation = (
    form: IContracts.IAddEditContractBasicSchema
  ): IContracts.AddEditContractBasicAPISchema => {
    return {
      entityId: form.entity.id,
      dailyWage: form.dailyWage,
      startDate: format(form.startDate, "yyyy-MM-dd'T'HH:mm:ss"),
      endDate: format(form.endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      ...(form.dailyWage === 1 && {
        dailyBased: {
          rate: form.dailyBased ? form.dailyBased.rate : undefined,
          startTime: form.dailyBased
            ? `${
                format(form.startDate, "yyyy-MM-dd'T'HH:mm:ss").split('T')[0]
              }T${
                format(
                  form.dailyBased.startTime ?? new Date(),
                  "yyyy-MM-dd'T'HH:mm:ss"
                ).split('T')[1]
              }`
            : undefined,
          endTime: form.dailyBased
            ? `${
                format(form.startDate, "yyyy-MM-dd'T'HH:mm:ss").split('T')[0]
              }T${
                format(
                  form.dailyBased.endTime ?? new Date(),
                  "yyyy-MM-dd'T'HH:mm:ss"
                ).split('T')[1]
              }`
            : undefined,
          extraHoursPricing: form.dailyBased?.hasExtraTime
            ? [
                {
                  hourPrice: form.dailyBased
                    ? form.dailyBased.extraHoursPricing?.[0]?.hourPrice
                    : undefined,
                  startTime: form.dailyBased
                    ? `${
                        format(form.startDate, "yyyy-MM-dd'T'HH:mm:ss").split(
                          'T'
                        )[0]
                      }T${
                        format(
                          form.dailyBased.endTime ?? new Date(),
                          "yyyy-MM-dd'T'HH:mm:ss"
                        ).split('T')[1]
                      }`
                    : undefined,
                  endTime: format(
                    setHours(setMinutes(form.startDate, 59), 23),
                    "yyyy-MM-dd'T'HH:mm:ss"
                  ),
                },
              ]
            : undefined,
        },
      }),
      ...(form.dailyWage === 0 && {
        unitBased: form?.unitBased?.map((unit) => ({
          unitId: unit.unitId,
          unitPrice: unit.unitPrice,
        })),
      }),
    } as IContracts.AddEditContractBasicAPISchema;
  };

  const getAddMutation = (
    form: IContracts.IAddContractSchema
  ): IContracts.IAddContractAPISchema => {
    return {
      ...getEditMutation(form),
      supplierId: form.supplier.id,
      jobsIds: form.jobs.map((job) => job.value),
    } as IContracts.IAddContractAPISchema;
  };

  const getResolveMismatchMutation = (
    values: IContracts.IAddContractSchema
  ): IContracts.IResolveUnitMismatchSchema => {
    return {
      entityId: values.entity.id,
      assignmentPricing: values?.unitBased
        ?.filter((unit) => unit.expanded)
        .map((unit) => ({
          id: unit.id,
          price: unit.unitPrice,
          unitId: unit.unitId,
        })),
    } as IContracts.IResolveUnitMismatchSchema;
  };

  // ********** Helpers **********/

  const parseTimeToDate = (timeValue: string | undefined): Date | undefined => {
    if (!timeValue) return undefined;
    const parsedDate = parse(timeValue, 'HH:mm:ss', new Date());
    return parsedDate;
  };

  const getCropLabelFormat = (
    list: IContracts.IJobsListSchema[] | undefined,
    name: string
  ) => {
    const item = list?.filter((item) => item.cropName === name);
    if (item?.length) {
      return (
        <div className='flex items-center gap-2'>
          <CircularBgImage
            imageSrc={item[0].cropImage}
            imageClassName='scale-125'
            bgColor='bg-outline-default'
            size='xxs'
          />
          <div>{item[0].cropName}</div>
        </div>
      );
    }
    return <span>{name}</span>;
  };

  const getJobLabelFormat = (
    list: IContracts.IJobsListSchema[] | undefined,
    jobId: number
  ) => {
    let selected: IContracts.IJobFormItemSchema | undefined = undefined;

    list?.map((crop) =>
      crop.jobs.map((job) => {
        if (job.jobId === jobId) {
          selected = {
            cropName: crop.cropName,
            cropImage: crop.cropImage,
            job,
          };
        }
      })
    );
    return selected;
  };

  const handleJobsListFormat = (
    list: IContracts.IJobsListSchema[] | undefined,
    startDateWatcher: Date
  ): GroupBase<IContracts.IOption>[] => {
    return (
      list?.map((item) => {
        return {
          label: item.cropName,
          options:
            item.jobs.map((job) => {
              return {
                label: job.taskName,
                cropImage: item.cropImage,
                cropName: item.cropName,
                value: job.jobId,
                durations: job.durations,
                isDisabled: checkForDate(startDateWatcher, job.durations ?? []),
              } as IContracts.IOption;
            }) ?? [],
        } as IContracts.IGroupOption;
      }) ?? []
    );
  };

  const checkForDate = (
    startDate: Date,
    durations: IContracts.IDurationSchema[]
  ): boolean => {
    let check = false;
    durations.map((duration) => {
      if (!check) {
        check = isWithinInterval(startDate, {
          start: new Date(duration.startDate),
          end: new Date(duration.endDate),
        });
      }
    });

    return check;
  };

  const { checkUnitMismatch } = useContractDetails();
  const checkCanEditContract = (contract: IContracts.IContractsListSchema) => {
    // Upcoming: always
    // Active: end-date != today, or if end-date == today but auto-generated/unit mismatch
    // Past: only if auto-generated or unit mismatch
    return (
      !contract.contractStatus ||
      checkUnitMismatch(
        contract.contractStatus,
        contract.dailyWage,
        contract.pricingUnits
      ) ||
      contract.status === 2 ||
      (contract.status === 1 && !isToday(new Date(contract.endDate)))
    );
  };

  return {
    openCreateContractModal,
    openEditContractModal,
    getContractGeneralInfoCardItems,
    getEditMutation,
    editContractValidationSchema,
    addContractValidationSchema,
    getFormSteps,
    getAddMutation,
    parseTimeToDate,
    getCropLabelFormat,
    getJobLabelFormat,
    handleJobsListFormat,
    checkForDate,
    checkCanEditContract,
    getResolveMismatchMutation,
  };
};
